/** @format */

// Sanctioning-body switcher rendered inside the top header. Hidden when
// the user has access to <= 1 body (single-body users see today's chrome).
// Two presentational variants — `desktop` for the inline cluster next to
// the logo, `mobile` for the popover-panel slot above Blog/Docs/Contact.

import { Link, useRouterState } from "@tanstack/react-router";
import type React from "react";

import {
  SANCTIONING_BODY_DEFAULT_LABEL,
  useSanctioningBodies,
  useVisibleSanctioningBodies,
} from "@/api/configApi";
import {
  pathForSanctioningBody,
  type SanctioningBodySlug,
} from "@/utils/sanctioningBody";

type Variant = "desktop" | "mobile";

interface NavItem {
  body: SanctioningBodySlug;
  label: string;
  to: string;
  active: boolean;
}

const isActiveForBody = (body: SanctioningBodySlug, pathname: string): boolean => {
  if (body === "nascar") {
    return pathname === "/";
  }
  return pathname === `/${body}`;
};

export const SanctioningBodyNav: React.FC<{ variant: Variant }> = ({ variant }) => {
  const visible = useVisibleSanctioningBodies();
  const sanctioningBodies = useSanctioningBodies();
  const pathname = useRouterState({ select: s => s.location.pathname });

  if (visible.length <= 1) {
    return null;
  }

  const items: NavItem[] = visible.map(body => ({
    body,
    label: sanctioningBodies[body]?.displayName ?? SANCTIONING_BODY_DEFAULT_LABEL[body],
    to: pathForSanctioningBody(body),
    active: isActiveForBody(body, pathname),
  }));

  if (variant === "desktop") {
    // Pill-style segmented switcher. Visually distinct from the
    // text-only Blog/Docs/Contact secondary nav, since this controls
    // *where in the app you are*, not *what external link you're hitting*.
    return (
      <nav
        className="hidden md:flex items-center rounded-md bg-slate-800/70 p-0.5 ring-1 ring-slate-700"
        aria-label="Sanctioning body"
      >
        {items.map(({ body, label, to, active }) => (
          <Link
            key={body}
            to={to}
            aria-current={active ? "page" : undefined}
            className={
              active
                ? "px-2.5 py-1 rounded-[5px] text-sm font-semibold bg-sky-600 text-white shadow-sm"
                : "px-2.5 py-1 rounded-[5px] text-sm font-medium text-slate-300 hover:text-white hover:bg-slate-700/60 transition-colors"
            }
          >
            {label}
          </Link>
        ))}
      </nav>
    );
  }

  // Mobile: dedicated section with a small label so the user understands
  // these are app-section links, not the same kind of link as Blog/Docs.
  return (
    <nav className="md:hidden border-b border-slate-700" aria-label="Sanctioning body">
      <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-500">
        Series
      </div>
      {items.map(({ body, label, to, active }) => (
        <Link
          key={body}
          to={to}
          aria-current={active ? "page" : undefined}
          className={
            active
              ? "block px-4 py-2 text-sm font-semibold text-white bg-sky-600/20 border-l-2 border-sky-500"
              : "block px-4 py-2 text-sm text-slate-200 hover:bg-slate-100 hover:text-slate-900 border-l-2 border-transparent"
          }
        >
          {label}
        </Link>
      ))}
    </nav>
  );
};

export default SanctioningBodyNav;
